import axios, { AxiosError } from 'axios';
import { BASE_API_URL } from '../../../lib/utils';
import { useMutation, useReactiveVar } from '@apollo/client';
import { tokenVar } from '../../../providers/token.service';
import { useCallback } from 'react';
import toast from 'react-hot-toast';
import download from 'downloadjs';
import { Identifiable, IDownloadFileParams } from '../../../lib/types';
import { IDownloadRequestAssetsParams } from './request.types';
import { START_REQUEST_DOWNLOAD } from './request.queries';

export interface IStartRequestDownloadParams extends IDownloadRequestAssetsParams, Identifiable {
  token?: string;
}

export const useStartRequestDownload = ({
  requestId: _id,
  requestToken: token
}: {
  requestId: string;
  requestToken?: string;
}) => {
  const [mutation, rest] = useMutation<{ startRequestDownload: string }, IStartRequestDownloadParams>(
    START_REQUEST_DOWNLOAD,
    { context: { serializationKey: 'MUTATION', tracked: true } }
  );

  const startRequestDownload = useCallback(
    (variables: IDownloadRequestAssetsParams) => mutation({ variables: { ...variables, _id, token } }),
    [mutation, _id, token]
  );

  return { startRequestDownload, ...rest };
};

const downloadRequest = async ({
  Authorization,
  fileName,
  onDownloadProgress,
  requestId,
  requestToken
}: IDownloadFileParams & { fileName: string; requestId: string }) => {
  if (!requestToken && !Authorization) return Promise.reject({ message: 'Unauthorized.' });

  return axios<Blob>({
    baseURL: BASE_API_URL,
    method: 'GET',
    url: `/api/request-assets/download/${requestId}/${fileName}`,
    headers: requestToken ? undefined : { Authorization },
    params: requestToken ? { requestToken } : undefined,
    responseType: 'blob',
    onDownloadProgress
  });
};

export const useDownloadRequestAssets = ({ requestId, requestToken }: { requestId: string; requestToken?: string }) => {
  const Authorization = useReactiveVar(tokenVar);
  const { startRequestDownload } = useStartRequestDownload({ requestId, requestToken });

  const startDownloadRequestAssets = useCallback(
    async (data: IDownloadRequestAssetsParams) => {
      return startRequestDownload(data).then((r) => {
        const toastId = 'download-' + data.fileName;
        toast.loading(data.fileName + ' started downloading...', { id: toastId, duration: 120000 });
        return r.data?.startRequestDownload;
      });
    },
    [startRequestDownload]
  );

  const downloadRequestAssets = useCallback(
    async (serverAssetName: string, downloadFileName: string) => {
      if (!requestToken && !Authorization) return Promise.reject({ message: 'Unauthorized.' });
      if (!requestId) return Promise.reject({ message: 'No file to download.' });

      const baseMessage = `Downloading PDF of ${downloadFileName}...`;
      const toastId = 'download-' + downloadFileName;
      toast.loading(baseMessage, { id: toastId, duration: 40000 });

      return downloadRequest({
        Authorization,
        fileName: serverAssetName,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.progress && progressEvent)
            if (progressEvent.progress < 1)
              toast.success(baseMessage + ' ' + Math.round(progressEvent.progress * 100) + '%', {
                id: toastId,
                duration: 5000
              });
            // Sleep a small amount so that failed count can catch up
            else {
              toast.success(downloadFileName + ' finished downloading!', { id: toastId, duration: 5000 });
            }
        },
        requestId,
        requestToken
      })
        .then((file) => {
          if (file) download(file.data, downloadFileName);
        })
        .catch(async (err) => {
          if (
            (
              JSON.parse((await (err as AxiosError<Blob>).response?.data.text()) ?? '{}') as unknown as {
                message?: string;
              }
            ).message
              ?.toLowerCase()
              .includes('too long')
          )
            toast.error(`Failed to download ${downloadFileName}, a file name was too long`, {
              id: toastId,
              duration: 5000
            });
          else toast.error(`Failed to download ${downloadFileName}`, { id: toastId, duration: 5000 });
        });
    },
    [Authorization, requestId, requestToken]
  );

  return { downloadRequestAssets, startDownloadRequestAssets };
};
